import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Copy, Loader2, Mail, MessageCircle, Ban, Link2, FileText } from "lucide-react";
import { format } from "date-fns";

const PUBLIC_APP_URL = "https://dashboard.creativeinvestments.africa";

const RECIPIENT_ROLES = ["Tour Guide", "Hotel", "Supplier", "Driver", "Client", "Other"];

const EXPIRY_OPTIONS = [
  { label: "24 hours", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export interface ShareableDocument {
  id: string;
  file_name: string;
  display_name?: string | null;
}

interface ShareDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  documents: ShareableDocument[];
  initialSelectedIds: string[];
}

const shareUrl = (token: string) => `${PUBLIC_APP_URL}/shared-documents/${token}`;

const sha256Hex = async (input: string) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export const ShareDocumentsDialog = ({
  open,
  onOpenChange,
  leadId,
  documents,
  initialSelectedIds,
}: ShareDocumentsDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [recipientName, setRecipientName] = useState("");
  const [recipientRole, setRecipientRole] = useState("Tour Guide");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [message, setMessage] = useState("");
  const [expiryDays, setExpiryDays] = useState("7");
  const [requireCode, setRequireCode] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState<{ id: string; token: string } | null>(null);
  const [accessCode, setAccessCode] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedIds(initialSelectedIds);
      setCreated(null);
      setAccessCode(null);
      setMessage("");
      setRecipientName("");
      setRecipientEmail("");
      setRecipientPhone("");
      setRequireCode(false);
      setExpiryDays("7");
      setRecipientRole("Tour Guide");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-share-picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, name, email, phone")
        .order("name");
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const { data: existingShares, refetch: refetchShares } = useQuery({
    queryKey: ["document-share-links", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_share_links")
        .select(
          "id, token, recipient_name, recipient_role, recipient_email, channel, expires_at, revoked_at, view_count, last_viewed_at, created_at, document_share_link_items(document_id)",
        )
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const selectedDocs = useMemo(
    () => documents.filter((d) => selectedIds.includes(d.id)),
    [documents, selectedIds],
  );

  const whatsappText = (token: string) => {
    const count = selectedDocs.length || 1;
    const lines = [
      `Hi ${recipientName || "there"},`,
      "",
      `Please find ${count} document${count === 1 ? "" : "s"} for our upcoming tour here:`,
      shareUrl(token),
    ];
    if (message.trim()) lines.splice(2, 0, message.trim(), "");
    return encodeURIComponent(lines.join("\n"));
  };

  const createShare = async () => {
    if (!recipientName.trim()) {
      toast({ title: "Recipient name required", variant: "destructive" });
      return null;
    }
    if (selectedIds.length === 0) {
      toast({ title: "Select at least one document", variant: "destructive" });
      return null;
    }

    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      let code: string | null = null;
      let codeHash: string | null = null;
      const token = crypto.randomUUID();

      if (requireCode) {
        code = String(Math.floor(100000 + Math.random() * 900000));
        codeHash = await sha256Hex(`${token}:${code}`);
      }

      const expiresAt = new Date(
        Date.now() + Number(expiryDays) * 24 * 60 * 60 * 1000,
      ).toISOString();

      const { data: share, error } = await supabase
        .from("document_share_links")
        .insert({
          token,
          lead_id: leadId,
          created_by: user?.id ?? null,
          recipient_name: recipientName.trim(),
          recipient_role: recipientRole,
          recipient_email: recipientEmail.trim() || null,
          recipient_phone: recipientPhone.trim() || null,
          message: message.trim() || null,
          channel: "link",
          require_code: requireCode,
          access_code_hash: codeHash,
          expires_at: expiresAt,
        })
        .select("id, token")
        .single();
      if (error) throw error;

      const { error: itemsError } = await supabase
        .from("document_share_link_items")
        .insert(selectedIds.map((document_id) => ({ share_link_id: share.id, document_id })));
      if (itemsError) throw itemsError;

      setCreated({ id: share.id, token: share.token });
      setAccessCode(code);
      refetchShares();
      queryClient.invalidateQueries({ queryKey: ["document-share-links", leadId] });
      return { id: share.id, token: share.token, code };
    } catch (err: any) {
      console.error("createShare failed:", err);
      toast({
        title: "Could not create share link",
        description: err.message,
        variant: "destructive",
      });
      return null;
    } finally {
      setCreating(false);
    }
  };

  const ensureShare = async () => {
    if (created) return { ...created, code: accessCode };
    return await createShare();
  };

  const handleCopyLink = async () => {
    const share = await ensureShare();
    if (!share) return;
    await navigator.clipboard.writeText(shareUrl(share.token));
    toast({
      title: "Link copied",
      description: share.code ? `Access code: ${share.code}` : "Paste it wherever you need.",
    });
  };

  const handleWhatsApp = async () => {
    const share = await ensureShare();
    if (!share) return;
    const phone = recipientPhone.replace(/\D/g, "");
    const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
    window.open(`${base}?text=${whatsappText(share.token)}`, "_blank", "noopener");
    await supabase.from("document_share_links").update({ channel: "whatsapp" }).eq("id", share.id);
    if (share.code) {
      toast({ title: "Send the access code separately", description: `Code: ${share.code}` });
    }
  };

  const handleEmail = async () => {
    if (!recipientEmail.trim()) {
      toast({ title: "Recipient email required", variant: "destructive" });
      return;
    }
    const share = await ensureShare();
    if (!share) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-document-share", {
        body: { shareId: share.id, accessCode: share.code },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast({
        title: "Email sent",
        description: `Documents shared with ${recipientEmail.trim()}`,
      });
      refetchShares();
    } catch (err: any) {
      console.error("send-document-share failed:", err);
      toast({ title: "Could not send email", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const revokeShare = async (id: string) => {
    const { error } = await supabase
      .from("document_share_links")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast({ title: "Could not revoke", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Share revoked" });
    refetchShares();
  };

  const activeShares = (existingShares || []).filter(
    (s: any) => !s.revoked_at && new Date(s.expires_at) > new Date(),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share documents externally</DialogTitle>
          <DialogDescription>
            Send a secure, expiring link to a tour guide, hotel or supplier. Files stay private —
            the link only opens the documents you select.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Recipient name</Label>
              <Input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="e.g. Sipho — guide"
                disabled={!!created}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={recipientRole} onValueChange={setRecipientRole} disabled={!!created}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECIPIENT_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!created && (
            <div className="space-y-1.5">
              <Label>Or pick a saved supplier / guide</Label>
              <Select
                onValueChange={(id) => {
                  const s = (suppliers || []).find((x: any) => x.id === id);
                  if (!s) return;
                  setRecipientName(s.name);
                  setRecipientEmail((s as any).email || "");
                  setRecipientPhone((s as any).phone || "");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select from suppliers" />
                </SelectTrigger>
                <SelectContent>
                  {(suppliers || []).map((s: any) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Email (for email sending)</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="guide@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile (for WhatsApp)</Label>
              <Input
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="+27 82 000 0000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Documents to share ({selectedIds.length})</Label>
            <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
              {documents.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-3 p-2 text-sm cursor-pointer hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selectedIds.includes(d.id)}
                    disabled={!!created}
                    onCheckedChange={(checked) =>
                      setSelectedIds((prev) =>
                        checked ? [...new Set([...prev, d.id])] : prev.filter((x) => x !== d.id),
                      )
                    }
                  />
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{d.display_name || d.file_name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Message (optional)</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please carry printed copies of these tickets for the 31 August park entry."
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Link expires in</Label>
              <Select value={expiryDays} onValueChange={setExpiryDays} disabled={!!created}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => (
                    <SelectItem key={o.days} value={String(o.days)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-2">
              <Switch
                id="require-code"
                checked={requireCode}
                onCheckedChange={setRequireCode}
                disabled={!!created}
              />
              <Label htmlFor="require-code" className="text-sm cursor-pointer">
                Require a 6-digit access code
              </Label>
            </div>
          </div>

          {created && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Link2 className="h-4 w-4" /> Share link ready
              </div>
              <code className="block break-all text-xs text-muted-foreground">
                {shareUrl(created.token)}
              </code>
              {accessCode && (
                <p className="text-xs">
                  Access code: <strong className="tracking-widest">{accessCode}</strong> — share
                  this separately from the link.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleEmail} disabled={creating || sending}>
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
              Send by email
            </Button>
            <Button variant="outline" onClick={handleCopyLink} disabled={creating}>
              <Copy className="h-4 w-4 mr-1" />
              Copy link
            </Button>
            <Button
              variant="outline"
              onClick={handleWhatsApp}
              disabled={creating}
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            >
              <MessageCircle className="h-4 w-4 mr-1" />
              WhatsApp
            </Button>
          </div>

          {activeShares.length > 0 && (
            <div className="space-y-2">
              <Label>Active external shares ({activeShares.length})</Label>
              <div className="rounded-md border divide-y">
                {activeShares.map((s: any) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 p-2 text-xs">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {s.recipient_name}{" "}
                        <span className="text-muted-foreground">({s.recipient_role})</span>
                      </p>
                      <p className="text-muted-foreground">
                        {(s.document_share_link_items || []).length} doc
                        {(s.document_share_link_items || []).length === 1 ? "" : "s"} ·{" "}
                        {s.view_count || 0} view{s.view_count === 1 ? "" : "s"} · expires{" "}
                        {format(new Date(s.expires_at), "MMM d, yyyy")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className="capitalize">{s.channel}</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigator.clipboard.writeText(shareUrl(s.token))}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => revokeShare(s.id)}>
                        <Ban className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
